import { useState } from 'react';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { AdminOrganization, AdminTenant, Plan } from '../../../types/domain';

interface UseAdminMutationsProps {
  plans: Plan[];
  setOrganizations: React.Dispatch<React.SetStateAction<AdminOrganization[]>>;
  refreshOrganizations: () => void;
  refreshTenants: () => void;
  refreshTelemetry: () => void;
}

export function useAdminMutations({
  plans,
  setOrganizations,
  refreshOrganizations,
  refreshTenants,
  refreshTelemetry,
}: UseAdminMutationsProps) {
  const { showToast } = useToast();
  const [updatingOrgId, setUpdatingOrgId] = useState<string | null>(null);

  const handleTierUpgrade = async (org: AdminOrganization, newTier: string) => {
    const matchedPlan = plans.find((p) => p.slug.toLowerCase() === newTier.toLowerCase());
    const maxSubmissions = matchedPlan ? matchedPlan.max_monthly_submissions : 500;
    const maxTables = matchedPlan ? matchedPlan.max_tables : 3;
    const maxSeats = matchedPlan ? matchedPlan.max_team_seats : 2;

    setUpdatingOrgId(org.id);

    try {
      await apiClient(`/admin/organizations/${org.id}/tier`, {
        method: 'PUT',
        body: JSON.stringify({
          plan_tier: newTier.toLowerCase(),
          max_monthly_submissions: maxSubmissions,
          max_tables: maxTables,
          max_team_seats: maxSeats,
        }),
      });

      showToast({
        type: 'success',
        title: 'Plan Tier Updated',
        message: `${org.name} upgraded to ${matchedPlan?.name || newTier} plan tier.`,
      });

      setOrganizations((prev) =>
        prev.map((o) =>
          o.id === org.id
            ? {
                ...o,
                plan_tier: newTier.toLowerCase(),
                max_monthly_submissions: maxSubmissions,
                max_tables: maxTables,
                max_team_seats: maxSeats,
              }
            : o
        )
      );
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Tier update failed',
        message: err?.message || 'Server error updating tier',
      });
    } finally {
      setUpdatingOrgId(null);
    }
  };

  const handleUpdateOrg = async (
    org: AdminOrganization,
    updates: {
      plan_tier: string;
      max_monthly_submissions: number;
      max_tables: number;
      max_team_seats: number;
    }
  ) => {
    setUpdatingOrgId(org.id);
    try {
      await apiClient(`/admin/organizations/${org.id}/tier`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      showToast({
        type: 'success',
        title: 'Organization Updated',
        message: `${org.name} subscription and quotas updated.`,
      });

      setOrganizations((prev) =>
        prev.map((o) =>
          o.id === org.id
            ? {
                ...o,
                plan_tier: updates.plan_tier,
                max_monthly_submissions: updates.max_monthly_submissions,
                max_tables: updates.max_tables,
                max_team_seats: updates.max_team_seats,
              }
            : o
        )
      );
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Failed to update organization quotas',
      });
      throw err;
    } finally {
      setUpdatingOrgId(null);
    }
  };

  const handleDeleteOrg = async (org: AdminOrganization) => {
    try {
      await apiClient(`/admin/organizations/${org.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Organization Deleted',
        message: `Organization "${org.name}" and all associated datastores and storage files were permanently removed.`,
      });

      refreshOrganizations();
      refreshTelemetry();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete organization',
      });
      throw err;
    }
  };

  const handleBulkDeleteOrgs = async (orgs: AdminOrganization[]) => {
    try {
      await apiClient('/admin/organizations/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ organization_ids: orgs.map((o) => o.id) }),
      });

      showToast({
        type: 'success',
        title: 'Organizations Deleted',
        message: `Successfully deleted ${orgs.length} ${
          orgs.length === 1 ? 'organization' : 'organizations'
        } and purged storage files.`,
      });

      refreshOrganizations();
      refreshTelemetry();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Bulk Deletion Failed',
        message: err?.message || 'Failed to delete selected organizations',
      });
      throw err;
    }
  };

  const handleDeleteTenant = async (tenant: AdminTenant) => {
    try {
      await apiClient(`/admin/tenants/${tenant.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'User Deleted',
        message: `User "${tenant.full_name || tenant.email}" was permanently removed.`,
      });

      refreshTenants();
      refreshTelemetry();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete user',
      });
      throw err;
    }
  };

  const handleBulkDeleteTenants = async (selectedTenants: AdminTenant[]) => {
    try {
      await apiClient('/admin/tenants/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ tenant_ids: selectedTenants.map((t) => t.id) }),
      });

      showToast({
        type: 'success',
        title: 'Users Deleted',
        message: `Successfully deleted ${selectedTenants.length} ${
          selectedTenants.length === 1 ? 'user' : 'users'
        }.`,
      });

      refreshTenants();
      refreshTelemetry();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Bulk Deletion Failed',
        message: err?.message || 'Failed to delete selected users',
      });
      throw err;
    }
  };

  return {
    updatingOrgId,
    handleTierUpgrade,
    handleUpdateOrg,
    handleDeleteOrg,
    handleBulkDeleteOrgs,
    handleDeleteTenant,
    handleBulkDeleteTenants,
  };
}
